"use client";

import { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { Compass, Heart, MapPin, Sparkles } from "lucide-react";
import type { Gift, GiftTimelineEvent } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  type MapJourneyExperienceData,
  type MapJourneyStopData,
} from "@/data/experienceDataSchema";

// Mapa real (Leaflet) só roda no client. Carrega via dynamic com fallback
// inerte enquanto hidrata — assim a página pública não quebra em SSR.
const MapJourneyRealMap = dynamic(
  () => import("@/components/map/MapJourneyRealMap"),
  {
    ssr: false,
    loading: () => (
      <div
        style={{
          height: 320,
          borderRadius: "0.75rem",
          background: "rgba(225,29,72,0.06)",
        }}
      />
    ),
  }
);

function pickGeoStops(
  stops: MapJourneyStopData[] | undefined
): MapJourneyStopData[] {
  if (!Array.isArray(stops)) return [];
  return stops.filter(
    (s) => typeof s?.lat === "number" && typeof s?.lng === "number"
  );
}

export type MapJourneyVariant = "default" | "spotify";

export interface MapJourneyPalette {
  accent: string;
  accentRgb: string;
}

interface MapJourneySectionProps {
  gift: Partial<Gift>;
  variant: MapJourneyVariant;
  palette?: MapJourneyPalette;
  className?: string;
}

interface JourneyStop {
  title: string;
  description: string;
  date?: string;
  kind: "start" | "memory" | "chapter" | "letter" | "destination";
}

const SPOTIFY_FALLBACK_PALETTE: MapJourneyPalette = {
  accent: "#1DB954",
  accentRgb: "29,185,84",
};

// ─── Helpers de dados — só usam campos já existentes no Gift ────────────────

function formatDateLabel(value?: string | null): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch {
    return value.slice(0, 10);
  }
}

function buildCustomJourneyStops(
  stops: MapJourneyExperienceData["stops"]
): JourneyStop[] | null {
  if (!Array.isArray(stops) || stops.length === 0) return null;
  const result: JourneyStop[] = [];
  for (let i = 0; i < stops.length && i < 5; i++) {
    const stop = stops[i] ?? {};
    const title = stop.title?.trim();
    const description = stop.description?.trim();
    if (!title && !description) continue;
    result.push({
      title: title || "Um capítulo especial",
      description:
        description || "Um detalhe dessa história que merece ser lembrado.",
      date: formatDateLabel(stop.date),
      kind: result.length === 0 ? "start" : "chapter",
    });
  }
  return result.length > 0 ? result : null;
}

function buildJourneyStops(gift: Partial<Gift>): JourneyStop[] {
  const stops: JourneyStop[] = [];

  stops.push({
    title: "Onde tudo começou",
    description: gift.coupleStartDate
      ? "A primeira página dessa história."
      : "O primeiro ponto simbólico dessa jornada.",
    date: formatDateLabel(gift.coupleStartDate),
    kind: "start",
  });

  const photos = gift.photos ?? [];
  if (photos.length > 0) {
    stops.push({
      title: "Memórias pelo caminho",
      description:
        photos.length === 1
          ? "Um momento registrado que virou parte da rota."
          : `${photos.length} momentos registrados que viraram parte da rota.`,
      kind: "memory",
    });
  }

  const events: GiftTimelineEvent[] = gift.timelineEvents ?? [];
  if (events.length > 0) {
    const featured = events[Math.floor(events.length / 2)] ?? events[0];
    const title = featured.title?.trim() || "Um capítulo especial";
    const description =
      featured.description?.trim() ||
      "Um detalhe dessa história que merece ser lembrado.";
    stops.push({
      title,
      description,
      date: formatDateLabel(featured.eventDate),
      kind: "chapter",
    });
  } else if (gift.loveLetter && gift.loveLetter.trim().length > 0) {
    stops.push({
      title: "Uma carta no caminho",
      description: "Uma mensagem guardada que virou parte dessa rota.",
      kind: "letter",
    });
  }

  if (stops.length < 3) {
    stops.push({
      title: "Um capítulo especial",
      description: "Um detalhe dessa história que merece ser lembrado.",
      kind: "chapter",
    });
  }

  stops.push({
    title: "Próximo destino",
    description: "Tudo que vocês ainda vão viver juntos.",
    kind: "destination",
  });

  return stops.slice(0, 5);
}

// ─── Layout do mapa simbólico ───────────────────────────────────────────────

function pinPositions(count: number): Array<{ x: number; y: number }> {
  const layouts: Record<number, Array<{ x: number; y: number }>> = {
    1: [{ x: 50, y: 50 }],
    2: [
      { x: 14, y: 38 },
      { x: 86, y: 58 },
    ],
    3: [
      { x: 10, y: 32 },
      { x: 50, y: 60 },
      { x: 90, y: 32 },
    ],
    4: [
      { x: 10, y: 30 },
      { x: 36, y: 60 },
      { x: 64, y: 32 },
      { x: 90, y: 62 },
    ],
    5: [
      { x: 8, y: 32 },
      { x: 28, y: 60 },
      { x: 50, y: 32 },
      { x: 72, y: 60 },
      { x: 92, y: 32 },
    ],
  };
  // 0 stops → array vazio (não desenha pin nenhum). count > 5 cai pra 5.
  // Fallback antigo `layouts[4]` causava o crash quando count=1: retornava
  // 4 positions mas stops só tinha 1 item → stops[idx] undefined no map.
  if (count <= 0) return [];
  return layouts[count] ?? layouts[5];
}

function buildRoutePath(count: number): string {
  const paths: Record<number, string> = {
    2: "M 14 38 Q 35 72 60 55 Q 75 42 86 58",
    3: "M 10 32 Q 28 76 50 60 Q 72 44 90 32",
    4: "M 10 30 Q 22 70 36 60 Q 50 50 64 32 Q 78 22 90 62",
    5: "M 8 32 Q 18 72 28 60 Q 40 32 50 32 Q 62 32 72 60 Q 82 78 92 32",
  };
  return paths[count] ?? paths[4];
}

function stopGlyph(kind: JourneyStop["kind"], className?: string) {
  switch (kind) {
    case "memory":
      return <Sparkles className={className} />;
    case "letter":
      return <Heart className={className} />;
    case "destination":
      return <Compass className={className} />;
    case "chapter":
    case "start":
    default:
      return <MapPin className={className} />;
  }
}

// ─── Componente principal ──────────────────────────────────────────────────

export function MapJourneySection({
  gift,
  variant,
  palette,
  className,
}: MapJourneySectionProps) {
  const customData = useMemo(
    () => getExperienceData<MapJourneyExperienceData>(gift, "map-journey"),
    [gift]
  );
  // Modo "mapa real": pelo menos uma parada tem lat/lng (o usuário marcou
  // no Leaflet do form). Quando não tem nenhuma, mantém o mapa simbólico
  // antigo (back-compat com gifts criados antes da feature).
  const geoStops = useMemo(
    () => pickGeoStops(customData?.stops),
    [customData]
  );
  const useRealMap = geoStops.length > 0;

  const stops = useMemo(() => {
    // Modo mapa real: as paradas da lista/card derivam DIRETO dos geoStops,
    // preservando a quantidade e a ordem real. Sem isso, stops sem título
    // eram filtrados em buildCustomJourneyStops e o componente caía no
    // fallback simbólico ("Onde tudo começou - Parada 1 de 1").
    if (useRealMap && geoStops.length > 0) {
      return geoStops.map((s, idx): JourneyStop => {
        const fallbackName = s.locationName?.split("—")[0]?.trim();
        return {
          title:
            s.title?.trim() || fallbackName || `Parada ${idx + 1}`,
          description:
            s.description?.trim() ||
            "Um capítulo dessa história — toque no pin no mapa.",
          date: formatDateLabel(s.date),
          kind:
            idx === 0
              ? "start"
              : idx === geoStops.length - 1
              ? "destination"
              : "chapter",
        };
      });
    }
    const custom = buildCustomJourneyStops(customData?.stops);
    if (custom) return custom;
    return buildJourneyStops(gift);
  }, [gift, customData, useRealMap, geoStops]);
  const positions = useMemo(() => pinPositions(stops.length), [stops.length]);
  const path = useMemo(() => buildRoutePath(stops.length), [stops.length]);

  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = Math.min(activeIndex, stops.length - 1);
  const activeStop = stops[safeIndex];
  const customTitle = customData?.title?.trim();
  const customSubtitle = customData?.subtitle?.trim();

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        stops={stops}
        positions={positions}
        path={path}
        activeIndex={safeIndex}
        onSelect={setActiveIndex}
        activeStop={activeStop}
        palette={palette ?? SPOTIFY_FALLBACK_PALETTE}
        className={className}
        title={customTitle}
        subtitle={customSubtitle}
        useRealMap={useRealMap}
        geoStops={geoStops}
      />
    );
  }

  return (
    <DefaultVariant
      stops={stops}
      positions={positions}
      path={path}
      activeIndex={safeIndex}
      onSelect={setActiveIndex}
      activeStop={activeStop}
      className={className}
      title={customTitle}
      subtitle={customSubtitle}
      useRealMap={useRealMap}
      geoStops={geoStops}
    />
  );
}

// ─── Variante default — clara, romântica, premium ───────────────────────────

interface VariantProps {
  stops: JourneyStop[];
  positions: Array<{ x: number; y: number }>;
  path: string;
  activeIndex: number;
  activeStop: JourneyStop;
  onSelect: (index: number) => void;
  className?: string;
  title?: string;
  subtitle?: string;
  // Quando true, exibe o mapa Leaflet em vez do SVG simbólico — usa as
  // coordenadas reais salvas em `geoStops` e mostra popup com foto/card.
  useRealMap?: boolean;
  geoStops?: MapJourneyStopData[];
}

interface SpotifyVariantProps extends VariantProps {
  palette: MapJourneyPalette;
}

function DefaultVariant({
  stops,
  positions,
  path,
  activeIndex,
  activeStop,
  onSelect,
  className,
  title,
  subtitle,
  useRealMap,
  geoStops,
}: VariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "map-journey-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FDE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
    >
      <Heart
        className="pointer-events-none absolute -top-4 -right-4 h-24 w-24 text-[#E11D48]/12"
        fill="currentColor"
      />
      <Heart
        className="pointer-events-none absolute -bottom-3 -left-3 h-14 w-14 text-[#F59E0B]/20"
        fill="currentColor"
      />
      <Sparkles className="pointer-events-none absolute top-5 left-5 h-4 w-4 text-[#E11D48]/40" />

      <div className="relative z-10 flex flex-col gap-5 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Compass className="h-3 w-3" />
            Jornada simbólica
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "Nossa Jornada no Mapa"}
          </h2>
          <p className="max-w-md text-xs sm:text-sm text-[#1F2937]/70 leading-relaxed">
            {subtitle ||
              "Uma rota imaginária pelos momentos que escreveram essa história — toque em cada parada para reviver um capítulo."}
          </p>
        </header>

        {useRealMap && geoStops && geoStops.length > 0 ? (
          <MapJourneyRealMap
            stops={geoStops}
            accent="#E11D48"
            accentRgb="225,29,72"
            variant="default"
            height={320}
          />
        ) : (
        <div className="relative rounded-2xl border border-white/70 bg-white/55 p-4 shadow-inner backdrop-blur">
          <MapTexture variant="default" />
          <CompassMark variant="default" />
          <div className="relative">
            <svg
              viewBox="0 0 100 80"
              preserveAspectRatio="none"
              className="block w-full h-48 sm:h-56"
              aria-hidden="true"
            >
              <path
                d={path}
                fill="none"
                stroke="#E11D48"
                strokeWidth="0.9"
                strokeOpacity="0.65"
                strokeDasharray="2.5 2.5"
                strokeLinecap="round"
              />
              {positions.map((pos, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <g key={idx} transform={`translate(${pos.x} ${pos.y})`}>
                    {isActive && (
                      <circle r="5.2" fill="#F59E0B" fillOpacity="0.18" />
                    )}
                    <circle
                      r={isActive ? 3.2 : 2.4}
                      fill={isActive ? "#F59E0B" : "#ffffff"}
                      stroke="#E11D48"
                      strokeWidth={isActive ? 1.2 : 1}
                    />
                    <circle r="0.9" fill="#881337" />
                  </g>
                );
              })}
            </svg>
            <div className="absolute inset-0">
              {positions.map((pos, idx) => {
                const stop = stops[idx];
                // Defesa em profundidade: mesmo com pinPositions retornando
                // o número certo agora, pula se stop faltar (race entre
                // memos / dados temporariamente inconsistentes).
                if (!stop) return null;
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => onSelect(idx)}
                    aria-label={`Parada ${idx + 1}: ${stop.title}`}
                    aria-pressed={isActive}
                    className="absolute grid h-7 w-7 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E11D48]/70"
                    style={{
                      left: `${pos.x}%`,
                      top: `${(pos.y / 80) * 100}%`,
                    }}
                  >
                    <span className="sr-only">{stop.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        )}

        <motion.article
          key={activeIndex}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-2xl border border-white/70 bg-white/85 p-4 sm:p-5 shadow-sm backdrop-blur"
        >
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337]/80">
            <span className="grid h-5 w-5 place-items-center rounded-full bg-[#E11D48] text-[10px] font-bold text-white">
              {activeIndex + 1}
            </span>
            Parada {activeIndex + 1} de {stops.length}
          </div>
          <h3 className="mt-2 flex items-center gap-2 text-lg font-semibold text-[#1F2937]">
            <span className="text-[#E11D48]">
              {stopGlyph(activeStop.kind, "h-4 w-4")}
            </span>
            {activeStop.title}
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-[#1F2937]/75">
            {activeStop.description}
          </p>
          {activeStop.date ? (
            <p className="mt-3 inline-flex rounded-full bg-[#FFE4E6] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337]">
              {activeStop.date}
            </p>
          ) : null}
        </motion.article>

        <ul className="flex flex-wrap justify-center gap-2">
          {stops.map((stop, idx) => {
            const isActive = idx === activeIndex;
            return (
              <li key={idx}>
                <button
                  type="button"
                  onClick={() => onSelect(idx)}
                  aria-pressed={isActive}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-colors",
                    isActive
                      ? "border-[#E11D48] bg-[#E11D48] text-white shadow-sm"
                      : "border-rose-200 bg-white/70 text-[#881337] hover:bg-white"
                  )}
                >
                  {idx + 1}. {stop.title}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.section>
  );
}

// ─── Variante Spotify — escura, imersiva ───────────────────────────────────

function SpotifyVariant({
  stops,
  positions,
  path,
  activeIndex,
  activeStop,
  onSelect,
  palette,
  className,
  title,
  subtitle,
  useRealMap,
  geoStops,
}: SpotifyVariantProps) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "map-journey-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(120% 100% at 0% 0%, rgba(${palette.accentRgb},0.22) 0%, rgba(${palette.accentRgb},0.06) 45%, rgba(15,15,18,0.95) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.25)`,
      }}
    >
      <Heart
        className="pointer-events-none absolute -top-2 -right-2 h-16 w-16 opacity-15"
        style={{ color: palette.accent }}
        fill="currentColor"
      />
      <Compass
        className="pointer-events-none absolute bottom-3 -left-2 h-10 w-10 opacity-10"
        style={{ color: palette.accent }}
      />

      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <Compass className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Jornada simbólica
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold leading-tight text-white">
            {title || "Nossa Jornada no Mapa"}
          </h3>
          <p className="text-[11px] leading-relaxed text-white/55">
            {subtitle ||
              "Uma rota imaginária pelos momentos que escreveram essa história."}
          </p>
        </div>

        {useRealMap && geoStops && geoStops.length > 0 ? (
          <MapJourneyRealMap
            stops={geoStops}
            accent={palette.accent}
            accentRgb={palette.accentRgb}
            variant="spotify"
            height={320}
          />
        ) : (
        <div
          className="relative overflow-hidden rounded-xl"
          style={{
            background: `linear-gradient(135deg, rgba(${palette.accentRgb},0.08) 0%, rgba(0,0,0,0.45) 100%)`,
            border: `1px solid rgba(${palette.accentRgb},0.18)`,
          }}
        >
          <MapTexture variant="spotify" />
          <CompassMark variant="spotify" accent={palette.accent} />
          <div className="relative">
            <svg
              viewBox="0 0 100 80"
              preserveAspectRatio="none"
              className="block w-full h-44 sm:h-48"
              aria-hidden="true"
            >
              <path
                d={path}
                fill="none"
                stroke={palette.accent}
                strokeWidth="0.9"
                strokeOpacity="0.7"
                strokeDasharray="2.5 2.5"
                strokeLinecap="round"
              />
              {positions.map((pos, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <g key={idx} transform={`translate(${pos.x} ${pos.y})`}>
                    {isActive && (
                      <circle
                        r="5.4"
                        fill={palette.accent}
                        fillOpacity="0.18"
                      />
                    )}
                    <circle
                      r={isActive ? 3.2 : 2.4}
                      fill={isActive ? palette.accent : "#0F0F12"}
                      stroke={palette.accent}
                      strokeWidth="1"
                    />
                    <circle
                      r="0.9"
                      fill={isActive ? "#0F0F12" : palette.accent}
                    />
                  </g>
                );
              })}
            </svg>
            <div className="absolute inset-0">
              {positions.map((pos, idx) => {
                const stop = stops[idx];
                if (!stop) return null;
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => onSelect(idx)}
                    aria-label={`Parada ${idx + 1}: ${stop.title}`}
                    aria-pressed={isActive}
                    className="absolute grid h-7 w-7 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2"
                    style={{
                      left: `${pos.x}%`,
                      top: `${(pos.y / 80) * 100}%`,
                      // @ts-expect-error tailwind ring color custom
                      "--tw-ring-color": `rgba(${palette.accentRgb},0.7)`,
                    }}
                  >
                    <span className="sr-only">{stop.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        )}

        <motion.article
          key={activeIndex}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="rounded-xl px-3 py-3 backdrop-blur"
          style={{
            background: `rgba(${palette.accentRgb},0.08)`,
            border: `1px solid rgba(${palette.accentRgb},0.18)`,
          }}
        >
          <div
            className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            <span
              className="grid h-4 w-4 place-items-center rounded-full text-[9px] font-bold text-[#0F0F12]"
              style={{ background: palette.accent }}
            >
              {activeIndex + 1}
            </span>
            Parada {activeIndex + 1} de {stops.length}
          </div>
          <h4 className="mt-1.5 flex items-center gap-1.5 text-sm font-semibold text-white">
            <span style={{ color: palette.accent }}>
              {stopGlyph(activeStop.kind, "h-3.5 w-3.5")}
            </span>
            {activeStop.title}
          </h4>
          <p className="mt-1 text-[12px] leading-relaxed text-white/75">
            {activeStop.description}
          </p>
          {activeStop.date ? (
            <p
              className="mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{
                color: palette.accent,
                background: `rgba(${palette.accentRgb},0.14)`,
                border: `1px solid rgba(${palette.accentRgb},0.22)`,
              }}
            >
              {activeStop.date}
            </p>
          ) : null}
        </motion.article>

        <ul className="flex flex-wrap gap-1.5">
          {stops.map((stop, idx) => {
            const isActive = idx === activeIndex;
            return (
              <li key={idx}>
                <button
                  type="button"
                  onClick={() => onSelect(idx)}
                  aria-pressed={isActive}
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors"
                  style={{
                    color: isActive ? "#0F0F12" : palette.accent,
                    background: isActive
                      ? palette.accent
                      : `rgba(${palette.accentRgb},0.08)`,
                    border: isActive
                      ? "none"
                      : `1px solid rgba(${palette.accentRgb},0.22)`,
                  }}
                >
                  {idx + 1}. {stop.title}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.section>
  );
}

// ─── Decoração de mapa simbólico ───────────────────────────────────────────

function MapTexture({ variant }: { variant: MapJourneyVariant }) {
  const isDark = variant === "spotify";
  return (
    <div
      className="pointer-events-none absolute inset-0 opacity-30"
      style={{
        backgroundImage: isDark
          ? "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.14) 1px, transparent 0)"
          : "radial-gradient(circle at 1px 1px, rgba(136,19,55,0.22) 1px, transparent 0)",
        backgroundSize: "12px 12px",
      }}
      aria-hidden="true"
    />
  );
}

function CompassMark({
  variant,
  accent,
}: {
  variant: MapJourneyVariant;
  accent?: string;
}) {
  const color =
    variant === "spotify" ? accent ?? "#1DB954" : "rgba(136,19,55,0.35)";
  return (
    <div
      className="pointer-events-none absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full"
      style={{
        border: `1px dashed ${color}`,
      }}
      aria-hidden="true"
    >
      <span
        className="text-[8px] font-bold tracking-widest"
        style={{ color }}
      >
        N
      </span>
    </div>
  );
}
